import { useEffect, useState } from "react";

import axios from "axios";

import { apiBaseUrls } from "@platform/constants/shared-constants";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface EnvironmentInfo {
  id: number;
  name: string;
  slug: string;
  envType: string;
  status: string;
  projectId: number;
  organizationId: number;
}

export interface StackInfo {
  id: number;
  alias: string;
  name: string;
  status: string;
  pipelineMode: string;
  templateSlug: string | null;
  vcsRepoUrl: string | null;
  environmentId: number | null;
}

interface PaginatedResponse {
  limit: number;
  offset: number;
  total: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

interface EnvironmentsResponse extends PaginatedResponse {
  environments: EnvironmentInfo[];
}

interface StacksResponse extends PaginatedResponse {
  applications: StackInfo[];
}

// ── Data Hooks ─────────────────────────────────────────────────────────────────

export function useEnvironments(orgAlias: string, projectAlias: string) {
  const [environments, setEnvironments] = useState<EnvironmentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    axios
      .get<EnvironmentsResponse>(`${apiBaseUrls.BACKEND}/api/v1/environments`, {
        params: { projectAlias },
        withCredentials: true,
      })
      .then((res) => setEnvironments(res.data.environments))
      .catch(() => setError("Failed to load environments"))
      .finally(() => setLoading(false));
  }, [orgAlias, projectAlias]);

  return { environments, loading, error };
}

export function useStacksForProject(orgAlias: string, projectAlias: string) {
  const [stacks, setStacks] = useState<StackInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get<StacksResponse>(`${apiBaseUrls.BACKEND}/api/v1/applications`, {
        params: { projectAlias },
        withCredentials: true,
      })
      .then((res) => setStacks(res.data.applications))
      .catch(() => setStacks([]))
      .finally(() => setLoading(false));
  }, [orgAlias, projectAlias]);

  return { stacks, loading };
}
